import { appStorage } from '../lib/safeStorage';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft, CalendarDays } from 'lucide-react';
import apiClient from '../services/apiClient';
import { toast } from 'sonner';
import SIDEBAR_MENU from '../config/sidebarMenu';
import { getVisibleSidebar } from '../lib/sidebarFilter';
import { searchGuides } from '../lib/guideRegistry';
import { startGuide } from '../lib/guideStore';
// Global command palette. Cmd/Ctrl+K (or a `staffed:command-palette` event)
// opens it; it flattens the role-filtered sidebar into a searchable jump list
// so the owner can reach any page without hunting through nested menus.
function buildCommands(user) {
  let sections = [];
  try { sections = getVisibleSidebar(SIDEBAR_MENU, user) || []; } catch (e) { sections = []; }
  const cmds = [];
  const seen = new Set();
  const push = (label, path, Icon, group) => {
    if (!label || !path || seen.has(path)) return;
    seen.add(path);
    cmds.push({ label, path, Icon, group });
  };
  sections.forEach((s) => {
    if (s.isTopLevel) push(s.label, s.path, s.icon, null);
    else if (Array.isArray(s.children)) s.children.forEach((c) => push(c.label, c.path, c.icon, s.label));
  });
  // Settings hub sections — the sidebar shows one "Settings" item, but each
  // section stays searchable here, each pointing at its hub route.
  const templatesOff = user?.entitlements?.templates === false;
  const hub = [
    { label: 'Business profile', path: '/settings/setup/business-profile' },
    { label: 'Branding', path: '/settings/setup/branding' },
    { label: 'Event types', path: '/settings/setup/event-types' },
    { label: 'Payment fees', path: '/settings/setup/payment-fees' },
    { label: 'Lead Capture Form', path: '/settings/setup/website-embed' },
    { label: 'Package contents', path: '/settings/setup/package-contents' },
  ];
  if (!templatesOff) {
    hub.push(
      { label: 'Packages', path: '/settings/setup/packages' },
      { label: 'Add-ons', path: '/settings/setup/add-ons' },
      { label: 'Proposal terms', path: '/settings/setup/proposal-terms' },
      { label: 'Contract terms', path: '/settings/setup/contract-terms' },
      { label: 'Day-of checklist', path: '/settings/setup/day-of-checklist' },
      { label: 'Templates', path: '/settings/setup/templates' },
    );
  }
  if (user?.isPlatformOwner === true) {
    hub.push({ label: 'Billing', path: '/settings/setup/billing' });
  }
  hub.forEach((h) => push(h.label, h.path, null, 'Settings'));
  return cmds;
}
export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const commands = useMemo(() => {
    if (!open) return [];
    let u = {};
    try { u = JSON.parse(appStorage.getItem('user') || '{}'); } catch (e) { /* ignore */ }
    return buildCommands(u);
  }, [open]);
  const pages = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    const terms = q.split(/\s+/);
    return commands.filter((c) => {
      const hay = `${c.label} ${c.group || ''} ${c.path}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [query, commands]);
  // Content search. Pages stay local and instant; events come from the server
  // because `GET /api/events` is a FILTERED subset (it hides past events and
  // un-booked lead shadows), so filtering it client-side would silently miss
  // exactly the events someone is most likely to be hunting for.
  const [remote, setRemote] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) { setRemote([]); setSearching(false); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await apiClient.get('/search/jump', { params: { q, limit: 6 } });
        if (cancelled) return;
        const rows = (res.data?.results || []).map((r) => ({
          label: r.label,
          sub: r.sub || '',
          path: r.path,
          group: r.date || r.eventType || 'Event',
          kind: 'event',
          key: `event:${r.id}`,
          lifecycleState: r.lifecycleState || '',
        }));
        setRemote(rows);
      } catch (e) {
        // A failed lookup must never blank the page list the palette already
        // had. Drop the remote half and leave pages working.
        if (!cancelled) setRemote([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, open]);
  // BLOCK S7 1.b — registry guides surface here under a "Guides" group.
  const guideRows = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return searchGuides(q).slice(0, 5).map((g) => ({
      label: g.title, group: 'Guides', kind: 'guide', guideId: g.id, key: `guide:${g.id}`,
    }));
  }, [query]);
  // Events first, then guides, then pages.
  const results = useMemo(
    () => [...remote, ...guideRows, ...pages.map((c) => ({ ...c, key: `page:${c.path}` }))],
    [remote, guideRows, pages],
  );
  const close = useCallback(() => { setOpen(false); setQuery(''); setActive(0); }, []);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); setOpen((o) => !o); }
    };
    const onOpenEvt = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('staffed:command-palette', onOpenEvt);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('staffed:command-palette', onOpenEvt); };
  }, []);
  useEffect(() => {
    let seen = false;
    let welcomeDone = false;
    try {
      seen = appStorage.getItem('staffed_cmdk_hint_seen') === '1';
      welcomeDone = !!(appStorage.getItem('staffed_tour_seen') || appStorage.getItem('staffed_tour_completed'));
    } catch (e) { /* ignore */ }
    if (seen || !welcomeDone) return undefined;
    const t = setTimeout(() => {
      toast('Tip: press ⌘K (Ctrl+K) to jump to any page', { duration: 5000 });
      try { appStorage.setItem('staffed_cmdk_hint_seen', '1'); } catch (e) { /* ignore */ }
    }, 4000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => { if (open) setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 30); }, [open]);
  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);
  const choose = useCallback((cmd) => {
    if (!cmd) return;
    if (cmd.kind === 'guide') {
      const m = window.location.pathname.match(/^\/event\/([^/]+)/);
      startGuide(cmd.guideId, m ? m[1] : null);
      close();
      return;
    }
    close();
    navigate(cmd.path);
  }, [close, navigate]);
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(results[active]); }
  };
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center px-4 pt-[14vh]"
      data-testid="command-palette"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{ background: 'rgba(2,5,15,0.6)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="w-full max-w-xl rounded-2xl overflow-hidden"
        onKeyDown={onKeyDown}
        style={{ background: 'linear-gradient(180deg,#0F1220,#0A0E18)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.8)' }}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/[0.07]">
          <Search className="w-4 h-4 text-white/40" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to an event or a page…"
            className="flex-1 bg-transparent outline-none text-[15px] text-white placeholder:text-white/30"
            data-testid="command-palette-input"
          />
          <kbd className="text-[10px] text-white/30 border border-white/10 rounded px-1.5 py-0.5">esc</kbd>
        </div>
        <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-2">
          {results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-white/35">
              {searching ? 'Searching…' : <>No matches for &ldquo;{query}&rdquo;</>}
            </p>
          ) : results.map((c, i) => {
            const Icon = c.kind === 'event' ? CalendarDays : c.Icon;
            const on = i === active;
            return (
              <button
                key={c.key || c.path}
                data-idx={i}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(c)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${on ? 'bg-[#D4AF37]/12' : 'hover:bg-white/[0.03]'}`}
                data-testid={`command-item-${i}`}
              >
                {Icon ? <Icon className={`w-4 h-4 ${on ? 'text-[#E7C96A]' : 'text-white/40'}`} /> : <span className="w-4" />}
                <span className={`text-[14px] ${on ? 'text-white' : 'text-white/75'}`}>{c.label}</span>
                {c.sub && <span className="text-[12px] text-white/35 truncate">{c.sub}</span>}
                {c.lifecycleState && c.lifecycleState !== 'active' && (
                  <span
                    className="text-[9px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(251,113,133,0.14)', color: '#FB7185' }}
                  >
                    {c.lifecycleState === 'paused' ? 'On Hold' : c.lifecycleState}
                  </span>
                )}
                {c.group && <span className="ml-auto text-[11px] text-white/30">{c.group}</span>}
                {on && <CornerDownLeft className="w-3.5 h-3.5 text-white/30 ml-2" />}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3 px-4 py-2 border-t border-white/[0.07] text-[10px] text-white/30">
          <span>↑↓ to move</span><span>↵ to open</span><span>esc to close</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
